// @ts-check

import { useMemo } from "react";
import { get } from "lodash";
import { useQuery } from "@apollo/client/react";
import { getTeamProfileImageUrl } from "src/util/group";
import URL, { url } from "src/consts/urlPaths";
import { getPinnedEntities } from "src/queries/getPinnedEntities.graphql";
import { getEntityGradient } from "src/util/gradient";
import { getTagBgImage } from "src/util/tags";
import { ENTITY_TYPES } from "src/consts/entityTypes";
import { useOrgBranding } from "src/hooks/orgConfig/hooks/useOrgBranding";
import { useUserPinnedItems } from "./userPreferences";

/**
 * @typedef {{
 *   id: string,
 *   entityType: user_prefs.PinnedItemEntityType,
 *   name: string,
 *   avatar: string | null,
 *   gradient?: string,
 *   link: string,
 *   shape: "circle" | "square" | "tag",
 *   type?: string,
 *   onUnpin: () => void,
 *   jobTitle?: string,
 *   tagTypeName?: string,
 * }} PinnedEntity
 */

/**
 * @param {user_prefs.PinnedItem[]} items
 * @param {user_prefs.PinnedItemEntityType} entityType
 * @returns {string[]}
 */
const getIdsByEntityType = (items, entityType) => {
  return items
    .filter((item) => item.entityType === entityType)
    .map((item) => item.id);
};

/**
 * Custom hook to get the user's pinned items with display metadata for the side nav.
 */
export const usePinnedEntities = () => {
  const { branding } = useOrgBranding();
  const { pinnedItems, togglePinnedItem } = useUserPinnedItems();

  // Fetch teams/people for pinned entities
  const { data: pinnedEntitiesData } = useQuery(getPinnedEntities, {
    fetchPolicy: "cache-and-network",
    variables: {
      teamIds: getIdsByEntityType(pinnedItems || [], "team"),
      personIds: getIdsByEntityType(pinnedItems || [], "person"),
      tagIds: getIdsByEntityType(pinnedItems || [], "tag"),
    },
    skip: !pinnedItems?.length,
  });

  /**
   * Make pinned entities for teams and people
   * @type {Array<PinnedEntity>}
   */
  const pinnedEntities = useMemo(() => {
    const teams = (pinnedEntitiesData?.teams || []).map(
      /** @return {PinnedEntity} */
      (team) => ({
        id: team.id,
        entityType: ENTITY_TYPES.TEAM,
        name: team.name,
        avatar: getTeamProfileImageUrl(get(team, "attributes")),
        gradient: getEntityGradient(ENTITY_TYPES.TEAM, branding, team),
        link: url(URL.TEAM_DETAILS_BASE_PATH, { id: team.id }),
        shape: "square",
        type: team.type,
        onUnpin: () =>
          togglePinnedItem({ id: team.id, entityType: ENTITY_TYPES.TEAM }),
      })
    );

    const people = (pinnedEntitiesData?.people || []).map(
      /** @return {PinnedEntity} */
      (person) => ({
        id: person.aggregateId,
        entityType: ENTITY_TYPES.PERSON,
        name: `${person.firstName} ${person.surname}`,
        avatar: person.avatar,
        gradient: getEntityGradient(ENTITY_TYPES.PERSON, branding, person),
        link: url(URL.PERSON_DETAILS_BASE_PATH, { id: person.aggregateId }),
        shape: "circle",
        jobTitle: person.jobTitle,
        onUnpin: () =>
          togglePinnedItem({
            id: person.aggregateId,
            entityType: ENTITY_TYPES.PERSON,
          }),
      })
    );

    const tags = (pinnedEntitiesData?.tags || []).map(
      /** @return {PinnedEntity} */
      (tag) => ({
        id: tag.id,
        entityType: ENTITY_TYPES.TAG,
        name: tag.displayValue,
        avatar: getTagBgImage(tag),
        gradient: getEntityGradient(ENTITY_TYPES.TAG, branding, tag),
        link: url(URL.TAG_DETAILS, { id: tag.id }),
        shape: "tag",
        tagTypeName: tag.tagTypeName,
        onUnpin: () =>
          togglePinnedItem({
            id: tag.id,
            entityType: ENTITY_TYPES.TAG,
          }),
      })
    );

    return [...teams, ...people, ...tags];
  }, [pinnedEntitiesData, branding, togglePinnedItem]);

  return { pinnedEntities };
};
